<script setup lang="ts">
import { computed } from 'vue';

const props = withDefaults(
    defineProps<{
        src?: string | null;
        name?: string | null;
        size?: 'sm' | 'md' | 'lg';
    }>(),
    { src: null, name: null, size: 'md' },
);

const letter = computed(
    () => props.name?.trim().charAt(0).toUpperCase() || 'C',
);

const gradients = [
    'linear-gradient(135deg, #0ea5e9 0%, #6d28d9 100%)',
    'linear-gradient(135deg, #2563eb 0%, #9333ea 100%)',
    'linear-gradient(135deg, #0284c7 0%, #4f46e5 100%)',
    'linear-gradient(135deg, #3b82f6 0%, #c026d3 100%)',
    'linear-gradient(135deg, #06b6d4 0%, #7c3aed 100%)',
    'linear-gradient(135deg, #1d4ed8 0%, #a855f7 100%)',
] as const;

const fallbackGradient = computed(() => {
    const name = props.name?.trim() || 'Highlink';
    let hash = 0;

    for (const character of name) {
        hash = (hash * 31 + (character.codePointAt(0) ?? 0)) >>> 0;
    }

    return gradients[hash % gradients.length];
});

const fallbackStyle = computed<Record<string, string>>(() => ({
    '--brand-avatar-gradient': fallbackGradient.value,
}));
</script>

<template>
    <span
        class="brand-avatar"
        :class="[`brand-avatar--${size}`, { 'brand-avatar--fallback': !src }]"
        :style="!src ? fallbackStyle : undefined"
    >
        <img v-if="src" :src="src" alt="" />
        <span v-else class="brand-avatar__letter">{{ letter }}</span>
    </span>
</template>

<style scoped>
.brand-avatar {
    box-sizing: border-box;
    display: inline-grid;
    place-items: center;
    flex: 0 0 auto;
    overflow: hidden;
    border: 1px solid
        color-mix(in srgb, var(--tg-theme-hint-color) 18%, transparent);
    background: color-mix(in srgb, var(--accent) 13%, var(--surface));
    color: var(--accent);
    font-weight: 650;
}
.brand-avatar--fallback {
    border-color: rgba(255, 255, 255, 0.2);
    background: var(--brand-avatar-gradient);
    color: #ffffff;
    box-shadow:
        inset 0 1px 0 rgba(255, 255, 255, 0.2),
        inset 0 -12px 24px rgba(16, 10, 55, 0.16);
}
.brand-avatar__letter {
    line-height: 1;
    text-shadow: 0 2px 8px rgba(12, 8, 38, 0.24);
}
.brand-avatar--sm {
    width: 36px;
    height: 36px;
    border-radius: 12px;
    font-size: 13px;
}
.brand-avatar--md {
    width: 48px;
    height: 48px;
    border-radius: 15px;
    font-size: 17px;
}
.brand-avatar--lg {
    width: 82px;
    height: 82px;
    border-radius: 25px;
    font-size: 28px;
}
.brand-avatar img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
}
.brand-avatar img[src^='/preview/club-'] {
    padding: 7px;
    background: #0b1420;
    object-fit: contain;
}
</style>
